import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Brain,
  KeyRound,
  LayoutDashboard,
  Lock,
  Mail,
  Shield,
  Sparkles,
  Users,
  Workflow,
} from 'lucide-react';

export type AuthPageId = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'verify-email';

export interface AuthScrollBlock {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  metric?: string;
}

export interface AuthPageStory {
  id: AuthPageId;
  eyebrow: string;
  headline: string;
  subtitle: string;
  stat: string;
  asideVariant: 'command' | 'workspace' | 'security' | 'integrity' | 'activation';
  scrollSections: AuthScrollBlock[];
  trustLine: string;
}

export const authPageStories: Record<AuthPageId, AuthPageStory> = {
  login: {
    id: 'login',
    eyebrow: 'Finance command center',
    headline: 'Access your finance command center',
    subtitle:
      'Live cashflow, GST compliance, and AI intelligence — operational the moment you sign in.',
    stat: '₹218L net · live',
    asideVariant: 'command',
    scrollSections: [
      {
        id: 'l1',
        title: 'Realtime cashflow',
        description: 'Revenue, expenses, and forecast in one operational graph.',
        icon: Activity,
        metric: '+18% QoQ',
      },
      {
        id: 'l2',
        title: 'AI anomaly feed',
        description: 'Variance detection and recommendations stream as you work.',
        icon: Brain,
        metric: '3 insights',
      },
      {
        id: 'l3',
        title: 'Operations pulse',
        description: 'Approvals, payroll, and GST events in a unified feed.',
        icon: Workflow,
        metric: 'Live',
      },
    ],
    trustLine: 'Bank-grade encryption · SOC-ready · GST-native',
  },
  register: {
    id: 'register',
    eyebrow: 'Workspace onboarding',
    headline: 'Build your AI finance workspace',
    subtitle:
      'Configure your operational environment — P&L, cashflow, compliance, and AI from day one.',
    stat: 'Setup · ~3 min',
    asideVariant: 'workspace',
    scrollSections: [
      {
        id: 'r1',
        title: 'Choose your workspace',
        description: 'Business operations or CA client portfolio — tailored from the start.',
        icon: LayoutDashboard,
      },
      {
        id: 'r2',
        title: 'AI setup sequence',
        description: 'Operational intelligence calibrates to your entity structure.',
        icon: Sparkles,
        metric: 'Auto',
      },
      {
        id: 'r3',
        title: 'Collaborative finance',
        description: 'Approvals, roles, and audit-ready workflows built in.',
        icon: Users,
        metric: 'Team-ready',
      },
    ],
    trustLine: 'No credit card · Encrypted · Built for India',
  },
  'forgot-password': {
    id: 'forgot-password',
    eyebrow: 'Secure recovery',
    headline: 'Secure recovery environment',
    subtitle:
      'Password reset flows through encrypted channels with verification intelligence.',
    stat: '256-bit transit',
    asideVariant: 'security',
    scrollSections: [
      {
        id: 'f1',
        title: 'Encrypted handoff',
        description: 'Reset links expire quickly and are tied to your verified email.',
        icon: Shield,
        metric: 'Secure',
      },
      {
        id: 'f2',
        title: 'Verification intelligence',
        description: 'We confirm identity before any credential change is applied.',
        icon: Mail,
      },
    ],
    trustLine: 'Never share reset links · CMNDF will never ask for OTP by phone',
  },
  'reset-password': {
    id: 'reset-password',
    eyebrow: 'Account integrity',
    headline: 'Rebuild secure access',
    subtitle:
      'Set a strong password — integrity checks run in realtime as you type.',
    stat: 'Strength · live',
    asideVariant: 'integrity',
    scrollSections: [
      {
        id: 'rs1',
        title: 'Password integrity',
        description: 'Entropy, breach checks, and policy rules before save.',
        icon: Lock,
        metric: 'Realtime',
      },
      {
        id: 'rs2',
        title: 'Session protection',
        description: 'Active sessions can be rotated after a successful reset.',
        icon: KeyRound,
      },
    ],
    trustLine: 'One-time link · Do not reuse old passwords',
  },
  'verify-email': {
    id: 'verify-email',
    eyebrow: 'Activation',
    headline: 'Activate operational intelligence',
    subtitle:
      'Confirm your email to initialize dashboards, AI engines, and compliance systems.',
    stat: '6-digit verify',
    asideVariant: 'activation',
    scrollSections: [
      {
        id: 'v1',
        title: 'Node verification',
        description: 'Your workspace nodes sync after email confirmation.',
        icon: Sparkles,
        metric: 'Step 2/3',
      },
      {
        id: 'v2',
        title: 'Dashboard initialization',
        description: 'Cashflow and KPI systems prepare during activation.',
        icon: Activity,
      },
    ],
    trustLine: 'Code expires in 10 minutes · Check spam folder',
  },
};

export function pathnameToAuthPageId(pathname: string): AuthPageId {
  if (pathname.startsWith('/register')) return 'register';
  if (pathname.startsWith('/forgot-password')) return 'forgot-password';
  if (pathname.startsWith('/reset-password')) return 'reset-password';
  if (pathname.startsWith('/verify-email')) return 'verify-email';
  return 'login';
}
